import React, { useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

// Tooltip generico per avvolgere QUALSIASI contenuto (a differenza di HelpTip,
// che e' un'icona "?" dedicata): usato per il valore integrale di una cella
// troncata (.cell-ellipsis) o abbreviata (es. una data senza i secondi), al
// posto del title nativo del browser — che ha font di sistema, ritardo non
// controllabile e nessuna coerenza con il tema chiaro/scuro dell'app.
//
// In portal su document.body, non dentro la cella: molte celle vivono in
// contenitori con overflow:hidden/auto (troncamento testo, scroll orizzontale
// della tabella) che taglierebbero un popover posizionato "dentro" quella
// gerarchia. Stesso approccio di UserMenu.js per il suo pannello.
//
// Solo hover (nessun focus/tabIndex): il valore integrale e' gia' presente nel
// DOM anche quando troncato visivamente (screen reader/copia-incolla lo
// leggono comunque), quindi il tooltip e' un extra visivo, non l'unico modo
// per accedere all'informazione — a differenza di HelpTip, non ha bisogno di
// occupare uno stop aggiuntivo nell'ordine di tabulazione di ogni riga.

const STYLE_ID = 'cell-tooltip-styles';
const CSS = `
.cell-tooltip-trigger { cursor: default; }
.cell-tooltip-pop {
  position: fixed; z-index: 60; max-width: 320px; padding: .5rem .7rem;
  background: var(--bg-glass-strong);
  backdrop-filter: blur(var(--blur)); -webkit-backdrop-filter: blur(var(--blur));
  border: 1px solid var(--border-default); border-radius: 10px;
  box-shadow: 0 8px 24px var(--shadow-color);
  color: var(--text-secondary); font-size: .78rem; font-weight: 400; line-height: 1.4;
  white-space: normal; word-break: break-word;
  opacity: 0; transform: translateY(-4px);
  transition: opacity .14s ease, transform .14s ease;
  pointer-events: none;
}
.cell-tooltip-pop.is-open { opacity: 1; transform: none; }
`;

function ensureStyles() {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = CSS;
  document.head.appendChild(s);
}

function Tooltip({ text, children, className }) {
  useEffect(ensureStyles, []);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const triggerRef = useRef(null);
  const popRef = useRef(null);

  const show = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ top: r.bottom + 6, left: r.left });
    setOpen(true);
  }, []);
  const hide = useCallback(() => setOpen(false), []);

  // Il popover puo' sforare a destra su una tabella larga (colonne verso il
  // bordo, es. le date piu' a destra): dopo il render, se esce dalla viewport
  // lo si sposta a sinistra quel tanto che basta, invece di tagliarlo.
  useLayoutEffect(() => {
    if (!open || !popRef.current) return;
    const r = popRef.current.getBoundingClientRect();
    const overflowX = r.right - (window.innerWidth - 8);
    if (overflowX > 0) {
      setPos((p) => (p ? { ...p, left: p.left - overflowX } : p));
    }
  }, [open]);

  if (!text) return <>{children}</>;

  return (
    <>
      <span
        ref={triggerRef}
        className={`cell-tooltip-trigger${className ? ` ${className}` : ''}`}
        onMouseEnter={show}
        onMouseLeave={hide}
      >
        {children}
      </span>
      {open && pos && createPortal(
        <span
          ref={popRef}
          className={`cell-tooltip-pop${open ? ' is-open' : ''}`}
          role="tooltip"
          style={{ top: pos.top, left: pos.left }}
        >
          {text}
        </span>,
        document.body
      )}
    </>
  );
}

export default Tooltip;
